import graphQLClient from 'config/graphql-instance';
import { gql } from 'graphql-request';
import { getAiOptions } from './queries';

export type GQLCreatePromptReturn = {
  createPrompt: AiOptions;
};

export type GQLUpdatePromptReturn = {
  updatePrompt: AiOptions;
};

export type GQLDeletePromptReturn = {
  deletePrompt: AiOptions;
};

export type GQLUpdateAiOptionsReturn = {
  updateAiOptions: AiOptions;
};

export type GQLCreatePromptPayload = {
  name: string;
  model: string;
  english: {
    description: string;
    content: string;
  };
  spanish?: {
    description: string;
    content: string;
  };
};

export type AiOptionsInput = {
  model_agent: string;
  model_vision: string;
  prompt_system: {
    english: string;
    spanish?: string;
  };
  prompt_screen: {
    english: string;
    spanish?: string;
  };
  prompt_image: {
    english: string;
    spanish?: string;
  };
  prompts: Array<{
    name: string;
    model: string;
    english: {
      description: string;
      content: string;
    };
    spanish?: {
      description: string;
      content: string;
    };
  }>;
};

export type GQLUpdatePromptPayload = {
  prompt: GQLCreatePromptPayload;
  promptId: string;
};

export type GQLDeletePromptPayload = {
  promptId: string;
};

export type GQLUpdateAiOptionsPayload = {
  model_agent: string;
  model_vision: string;
  prompt_system: {
    english: string;
    spanish?: string;
  };
  prompt_screen: {
    english: string;
    spanish?: string;
  };
  prompt_image: {
    english: string;
    spanish?: string;
  };
};

export type AiOptions = {
  model_agent: string;
  model_vision: string;
  prompt_system: {
    english: string;
    spanish?: string;
  };
  prompt_screen: {
    english: string;
    spanish?: string;
  };
  prompt_image: {
    english: string;
    spanish?: string;
  };
  prompts?: Array<{
    _id: string;
    name: string;
    model: string;
    english: {
      description: string;
      content: string;
    };
    spanish?: {
      description: string;
      content: string;
    };
  }>;
};

export type AiOptionsWithoutPrompts = {
  model_agent: string;
  model_vision: string;
  prompt_system: {
    english: string;
    spanish?: string;
  };
  prompt_screen: {
    english: string;
    spanish?: string;
  };
  prompt_image: {
    english: string;
    spanish?: string;
  };
};

export const createPrompt = async (payload: GQLCreatePromptPayload) => {
  const mutation = gql`
    mutation CreatePrompt($prompt: CreatePromptInput!) {
      createPrompt(prompt: $prompt) {
        model_agent
        model_vision
        prompt_system {
          english
          spanish
        }
        prompt_screen {
          english
          spanish
        }
        prompt_image {
          english
          spanish
        }
        prompts {
          _id
          name
          model
          english {
            description
            content
          }
          spanish {
            description
            content
          }
        }
      }
    }
  `;
  const response = await graphQLClient().request<GQLCreatePromptReturn>(mutation, { prompt: payload });
  return response;
};

export const updatePrompt = async (payload: GQLUpdatePromptPayload) => {
  const mutation = gql`
    mutation UpdatePrompt($prompt: UpdatePromptInput!, $promptId: String!) {
      updatePrompt(prompt: $prompt, promptId: $promptId) {
        model_agent
        model_vision
        prompt_system {
          english
          spanish
        }
        prompt_screen {
          english
          spanish
        }
        prompt_image {
          english
          spanish
        }
        prompts {
          _id
          name
          model
          english {
            description
            content
          }
          spanish {
            description
            content
          }
        }
      }
    }
  `;
  const response = await graphQLClient().request<GQLUpdatePromptReturn>(mutation, payload);
  return response;
};

export const deletePrompt = async (promptId: string) => {
  const mutation = gql`
    mutation DeletePrompt($promptId: String!) {
      deletePrompt(promptId: $promptId) {
        model_agent
        model_vision
        prompt_system {
          english
          spanish
        }
        prompt_screen {
          english
          spanish
        }
        prompt_image {
          english
          spanish
        }
        prompts {
          _id
          name
          model
          english {
            description
            content
          }
          spanish {
            description
            content
          }
        }
      }
    }
  `;
  const response = await graphQLClient().request<GQLDeletePromptReturn, GQLDeletePromptPayload>(mutation, {
    promptId,
  });
  return response;
};

export const updateAiOptions = async (payload: GQLUpdateAiOptionsPayload) => {
  const mutation = gql`
    mutation UpdateAiOptions($input: UpdateAiOptionsInput!) {
      updateAiOptions(input: $input) {
        model_agent
        model_vision
        prompt_system {
          english
          spanish
        }
        prompt_screen {
          english
          spanish
        }
        prompt_image {
          english
          spanish
        }
        prompts {
          _id
          name
          model
          english {
            description
            content
          }
          spanish {
            description
            content
          }
        }
      }
    }
  `;
  const response = await graphQLClient().request(mutation, {
    input: payload,
  });
  return response;
};

export const refetchAiOptions = async () => {
  return getAiOptions();
};
